import { useState } from 'react';
import api from '../api/axios';
import { T } from './UI';

const SITES = ['CS Myaree', 'CS Midland'];
const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (d) => (d ? new Date(d).toLocaleString('en-AU', { day:'2-digit', month:'short', hour:'numeric', minute:'2-digit', hour12:true }) : '');

// Opens a PDF from the server (needs the login, so it's fetched and shown from memory)
export async function openInvoicePdf(walkInId) {
  const { data } = await api.get(`/walkins/${walkInId}/invoice/pdf`, { responseType:'blob' });
  window.open(URL.createObjectURL(data), '_blank', 'noopener');
}

// "Send tax invoice" dialog for one walk-in. onSent(invoiceSummary, email) lets the page update its row.
export default function SendInvoiceModal({ walkIn, defaultSite, onClose, onSent, onWalkInChanged }) {
  const existing = walkIn.invoice;
  const guess = SITES.find(s => new RegExp(s.replace('CS ', ''), 'i').test(`${walkIn.location || ''} ${defaultSite || ''}`)) || '';

  const [email, setEmail]   = useState(existing?.sentTo || walkIn.customerEmail || '');
  const [site, setSite]     = useState(guess);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(null);
  const [done, setDone]     = useState(null);
  const [paid, setPaid]     = useState(walkIn.status === 'paid');   // invoices only go out once it's paid
  const [marking, setMarking] = useState(false);

  const total = Number(walkIn.price) || 0;
  const gst = Math.round((total / 11) * 100) / 100;
  const noPrice = !(total > 0);
  const services = walkIn.services?.length ? walkIn.services : [walkIn.service].filter(Boolean);

  const markPaid = async () => {
    setMarking(true); setError(null);
    try {
      const { data } = await api.patch(`/walkins/${walkIn._id}`, { status: 'paid' });
      setPaid(true);
      onWalkInChanged?.(data.walkIn);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not mark it as paid. Please try again.');
    } finally { setMarking(false); }
  };

  const send = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post(`/walkins/${walkIn._id}/invoice/send`, { email: email.trim(), location: site });
      setDone(data.invoice);
      onSent?.(data.invoice, email.trim().toLowerCase());
    } catch (err) {
      const inv = err.response?.data?.invoice;
      if (inv) onSent?.(inv, email.trim().toLowerCase());   // issued but not delivered — the number exists now
      setError(err.response?.data?.message || 'Could not send the invoice. Please try again.');
    } finally { setBusy(false); }
  };

  const row = { display:'flex', justifyContent:'space-between', gap:'12px', fontSize:'13px', padding:'4px 0' };
  const label = { fontSize:'11px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'.5px', color:'var(--muted)', marginBottom:'5px', display:'block' };

  return (
    <div onClick={busy ? undefined : onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
      <div role="dialog" aria-label="Send tax invoice" onClick={e => e.stopPropagation()}
        style={{ background:'var(--surface)', borderRadius:'12px', width:'100%', maxWidth:'440px', maxHeight:'92vh', overflowY:'auto', boxShadow:'0 8px 32px rgba(0,0,0,0.25)' }}>
        <div style={{ display:'flex', alignItems:'center', padding:'14px 18px', borderBottom:'1px solid var(--border)' }}>
          <i className="ti ti-file-invoice" style={{ fontSize:'20px', color:'var(--blue)', marginRight:'8px' }} aria-hidden="true"/>
          <div style={{ flex:1, fontSize:'15px', fontWeight:'600', color:'var(--text)' }}>{existing ? `Tax invoice ${existing.number}` : 'Send tax invoice'}</div>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close" style={{ border:'none', background:'none', cursor:'pointer', fontSize:'22px', color:'var(--muted)', lineHeight:1 }}>×</button>
        </div>

        <div style={{ padding:'16px 18px 18px' }}>
          {done ? (
            <div>
              <div style={{ background:'var(--green-bg)', border:'1px solid rgba(15,110,86,0.25)', borderRadius:'var(--radius)', padding:'14px', color:'var(--green)', fontSize:'14px', marginBottom:'12px' }}>
                <i className="ti ti-circle-check" aria-hidden="true"/> Invoice <strong>{done.number}</strong> was sent to <strong>{done.sentTo}</strong>.
              </div>
              {!done.pdfAttached && <div style={{ fontSize:'12px', color:'var(--amber)', marginBottom:'8px', lineHeight:1.5 }}>The PDF couldn’t be attached this time, so the full invoice is shown in the email itself.</div>}
              {done.sentFrom && !/^invoices@/i.test(done.sentFrom) && <div style={{ fontSize:'12px', color:'var(--amber)', marginBottom:'8px', lineHeight:1.5 }}>It was sent from {done.sentFrom}, because invoices@carsaloon.com.au isn’t set up as a sender yet.</div>}
              <div style={{ display:'flex', gap:'8px', marginTop:'14px' }}>
                <button type="button" onClick={() => openInvoicePdf(walkIn._id)} style={{ ...T.btnSecondary, flex:1 }}>View PDF</button>
                <button type="button" onClick={onClose} style={{ ...T.btnPrimary, flex:1, justifyContent:'center' }}>Done</button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'12px 14px', marginBottom:'14px' }}>
                <div style={row}><span style={{ color:'var(--muted)' }}>Customer</span><strong>{walkIn.customerName || '—'}</strong></div>
                <div style={row}><span style={{ color:'var(--muted)' }}>Rego</span><strong style={{ fontFamily:'JetBrains Mono,monospace' }}>{walkIn.rego || '—'}</strong></div>
                <div style={{ ...row, alignItems:'flex-start' }}><span style={{ color:'var(--muted)' }}>Services</span><span style={{ textAlign:'right' }}>{services.map(s => <div key={s}>{s}</div>)}</span></div>
                <div style={{ ...row, borderTop:'1px solid var(--border)', marginTop:'6px', paddingTop:'8px' }}><span style={{ color:'var(--muted)' }}>GST included</span><span>{money(existing ? existing.gst : gst)}</span></div>
                <div style={row}><span style={{ fontWeight:'600' }}>Total (incl. GST)</span><strong style={{ fontSize:'15px', color:'var(--green)' }}>{money(existing ? existing.total : total)}</strong></div>
              </div>

              {existing && (
                <div style={{ fontSize:'12px', color: existing.sendStatus === 'sent' ? 'var(--green)' : 'var(--red)', marginBottom:'12px', lineHeight:1.5 }}>
                  {existing.sendStatus === 'sent'
                    ? <>Already sent to {existing.sentTo} on {fmt(existing.sentAt)}. Sending again uses the same invoice number and amount.</>
                    : <>Invoice {existing.number} was created but hasn’t been delivered yet{existing.lastError ? ` (${existing.lastError})` : ''}.</>}
                </div>
              )}

              {!paid && (
                <div style={{ background:'var(--amber-bg)', border:'1px solid rgba(133,79,11,0.2)', borderRadius:'var(--radius)', padding:'10px 12px', fontSize:'12px', color:'var(--amber)', marginBottom:'12px', lineHeight:1.5 }}>
                  <div>Invoices are only sent once the walk-in is <strong>Paid</strong>. Its status is <strong style={{ textTransform:'capitalize' }}>{walkIn.status}</strong> right now.</div>
                  <button type="button" onClick={markPaid} disabled={marking}
                    style={{ ...T.btnPrimary, marginTop:'8px', padding:'7px 12px', fontSize:'12px', opacity: marking ? 0.6 : 1 }}>
                    <i className="ti ti-check" aria-hidden="true"/> {marking ? 'Saving…' : 'Mark as Paid'}
                  </button>
                </div>
              )}

              {noPrice && !existing && <div style={{ background:'var(--amber-bg)', border:'1px solid rgba(133,79,11,0.2)', borderRadius:'var(--radius)', padding:'10px 12px', fontSize:'12px', color:'var(--amber)', marginBottom:'12px' }}>This walk-in has no price yet. Edit it and add the total price first.</div>}

              <label style={label} htmlFor="inv-email">Customer’s email</label>
              <input id="inv-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com" autoFocus
                style={{ ...T.input, marginBottom:'12px' }}/>

              {!existing && (
                <>
                  <label style={label} htmlFor="inv-site">Invoice for site</label>
                  <select id="inv-site" value={site} onChange={e => setSite(e.target.value)} style={{ ...T.input, marginBottom:'4px' }}>
                    <option value="">Choose…</option>
                    {SITES.map(s => <option key={s} value={s}>{s}  (numbers start {s.includes('Myaree') ? 'MY' : 'MI'}-)</option>)}
                  </select>
                </>
              )}

              {error && <div role="alert" style={{ background:'var(--red-bg)', border:'1px solid rgba(163,45,45,0.2)', borderRadius:'var(--radius)', padding:'10px 12px', fontSize:'12px', color:'var(--red)', margin:'12px 0 0', lineHeight:1.5 }}>{error}</div>}

              <div style={{ display:'flex', gap:'8px', marginTop:'16px' }}>
                {existing && <button type="button" onClick={() => openInvoicePdf(walkIn._id).catch(() => setError('Could not open the PDF'))} style={{ ...T.btnSecondary }}>View PDF</button>}
                <button type="button" onClick={onClose} disabled={busy} style={{ ...T.btnSecondary, flex:1 }}>Cancel</button>
                <button type="button" onClick={send} disabled={busy || !paid || !email.trim() || (!existing && (noPrice || !site))}
                  style={{ ...T.btnPrimary, flex:2, justifyContent:'center', opacity: busy || !paid || !email.trim() || (!existing && (noPrice || !site)) ? 0.55 : 1 }}>
                  <i className="ti ti-send" aria-hidden="true"/> {busy ? 'Sending…' : existing ? 'Send again' : 'Send invoice'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
